import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { fetchSalesDataApi } from "../api/viewApi";

function formatFirestoreTimestamp(ts) {
  if (!ts) {
    console.log("Invalid Date");
    return null;
  }
  const date = new Date(ts._seconds * 1000 + ts._nanoseconds / 1e6);
  return date.toString().split("GMT")[0].trim();
}

const PublicInvoicePage = () => {
  const [loading, setLoading] = useState(false);
  const [salesData, setSalesData] = useState(null);
  const [expandedItems, setExpandedItems] = useState({});
  const [expandedTaxInfo, setExpandedTaxInfo] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [Store,setStore]=useState("")

  const { key } = useParams();
  
  // console.log(key,"keye")

  const fetchSalesData = async () => {
    try {
      setLoading(true);
      const response = await fetchSalesDataApi(key);
      setSalesData(response.saleData || {});
      setStore(response.saleData.Store)
    } catch (error) {
      console.error("sales data error", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSalesData();
  }, []);

  const toggleItemDetails = (index) => {
    setExpandedItems((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const toggleTaxInfo = () => {
    setExpandedTaxInfo((prev) => !prev);
  };

  const handleDownloadPDF = async () => {
    try {
      setIsDownloading(true);

      // Dynamic imports
      const { jsPDF } = await import("jspdf");
      await import("jspdf-autotable");

      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      let yPos = 20;
      const pageWidth = pdf.internal.pageSize.getWidth();
      const margin = 15;

      // Helper function to add text with word wrap
      const addWrappedText = (text, x, y, maxWidth, fontSize = 10) => {
        pdf.setFontSize(fontSize);
        const lines = pdf.splitTextToSize(text, maxWidth);
        pdf.text(lines, x, y);
        return lines.length * (fontSize * 0.35);
      };

      // Header - Store Name
      pdf.setFontSize(16);
      pdf.setFont("helvetica", "bold");
      pdf.text(Store.StoreName, pageWidth / 2, yPos, { align: "center" });
      yPos += 8;

      // Store Address
      pdf.setFontSize(9);
      pdf.setFont("helvetica", "normal");
      const addressText = `${Store.Address1} ${Store.Address2}, ${Store.City} ${Store.State} ${Store.PostalCode}`;
      const addressHeight = addWrappedText(
        addressText,
        pageWidth / 2,
        yPos,
        pageWidth - 40,
        9
      );
      yPos += addressHeight + 5;

      // Line separator
      pdf.setLineWidth(0.5);
      pdf.line(margin, yPos, pageWidth - margin, yPos);
      yPos += 8;

      // Customer Details Section
      pdf.setFontSize(11);
      pdf.setFont("helvetica", "bold");
      pdf.text("Customer Details:", margin, yPos);
      yPos += 6;

      pdf.setFontSize(10);
      pdf.setFont("helvetica", "normal");
      pdf.text(
        `Name: ${salesData.Customer?.FirstName || ""} ${
          salesData.Customer?.LastName || ""
        }`,
        margin,
        yPos
      );
      yPos += 5;
      pdf.text(`Mobile: ${salesData.Customer?.Mobile || "N/A"}`, margin, yPos);
      yPos += 5;
      pdf.text(`GST Invoice No.: ${salesData.TransactionNo}`, margin, yPos);
      yPos += 8;

      // Store Name again
      pdf.setFontSize(10);
      pdf.setFont("helvetica", "bold");
      pdf.text(`Store Name: ${Store.StoreName}`, margin, yPos);
      yPos += 8;

      // Order Date
      pdf.setFont("helvetica", "normal");
      const orderDate = new Date(
        salesData.TransactionDate?._seconds * 1000
      ).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
      pdf.text(`Order Date: ${orderDate}`, margin, yPos);
      yPos += 10;

      // Calculate subtotal without tax
      const subtotalWithoutTax = salesData.LineItems.reduce(
        (sum, item) => sum + (item.SubTotalWithDiscount || 0),
        0
      );

      // Product Table - FIXED: Ensure data is properly formatted
      const productTableData = salesData.LineItems.map((item) => {
        const taxAmount =
          item.TaxComponentAmount?.reduce(
            (sum, t) => sum + (t.TaxAmount || 0),
            0
          ) || 0;

        return [
          item.Product?.Name || "N/A",
          item.Product?.HSNCode || "N/A",
          item.Qty?.toString() || "0",
          `${Store.CurrencySymbol}${(item.Price || 0).toFixed(2)}`,
          item.DiscountPer ? `${item.DiscountPer}%` : "0%",
          `${Store.CurrencySymbol}${(item.DiscountAmount || 0).toFixed(2)}`,
          `${Store.CurrencySymbol}${taxAmount.toFixed(2)}`,
          `${Store.CurrencySymbol}${(item.Amount || 0).toFixed(2)}`,
        ];
      });

      // FIXED: Check if autoTable function exists and data is valid
      if (pdf.autoTable && productTableData.length > 0) {
        pdf.autoTable({
          startY: yPos,
          head: [
            [
              "Product Name",
              "Item HSN",
              "Item Quantity",
              "MRP",
              "Item discount %",
              "Item discount",
              "Total Tax",
              "Net Amt",
            ],
          ],
          body: productTableData,
          theme: "grid",
          headStyles: {
            fillColor: [220, 220, 220],
            textColor: [0, 0, 0],
            fontStyle: "bold",
            fontSize: 8,
            halign: "center",
          },
          bodyStyles: {
            fontSize: 8,
            textColor: [0, 0, 0],
          },
          columnStyles: {
            0: { cellWidth: 35 },
            1: { cellWidth: 20, halign: "center" },
            2: { cellWidth: 18, halign: "center" },
            3: { cellWidth: 18, halign: "right" },
            4: { cellWidth: 18, halign: "center" },
            5: { cellWidth: 20, halign: "right" },
            6: { cellWidth: 18, halign: "right" },
            7: { cellWidth: 20, halign: "right" },
          },
          margin: { left: margin, right: margin },
          styles: { overflow: "linebreak" },
          pageBreak: "auto",
        });

        yPos = pdf.lastAutoTable.finalY + 10;
      } else {
        // Fallback if autoTable is not available
        console.warn("autoTable not available, using fallback");
        yPos += 10;
      }

      // Continue with the rest of your PDF content...
      // Basic Amount Total
      pdf.setFontSize(10);
      pdf.setFont("helvetica", "bold");
      pdf.text("Basic Amount Total", margin, yPos);
      pdf.text(
        `${Store.CurrencySymbol}${subtotalWithoutTax.toFixed(2)}`,
        pageWidth - margin,
        yPos,
        { align: "right" }
      );
      yPos += 8;

      // Tax Summary Section
      if (
        salesData.TaxSummaryList &&
        salesData.TaxSummaryList.length > 0 &&
        pdf.autoTable
      ) {
        pdf.setFontSize(11);
        pdf.setFont("helvetica", "bold");
        pdf.text("Tax Summary:", margin, yPos);
        yPos += 6;

        const taxTableData = [];
        salesData.TaxSummaryList.forEach((taxGroup) => {
          taxGroup.TaxComponentAmount?.forEach((comp) => {
            taxTableData.push([
              taxGroup.TaxGroup || "N/A",
              comp.TaxName || "N/A",
              `${comp.TaxPercentage || 0}%`,
              `${Store.CurrencySymbol}${(comp.TaxableAmount || 0).toFixed(2)}`,
              `${Store.CurrencySymbol}${(comp.TaxAmount || 0).toFixed(2)}`,
            ]);
          });
        });

        if (taxTableData.length > 0) {
          pdf.autoTable({
            startY: yPos,
            head: [["HSN", "Tax", "Rate", "Taxable Amount", "Tax Amount"]],
            body: taxTableData,
            theme: "grid",
            headStyles: {
              fillColor: [220, 220, 220],
              textColor: [0, 0, 0],
              fontStyle: "bold",
              fontSize: 9,
              halign: "center",
            },
            bodyStyles: {
              fontSize: 9,
              textColor: [0, 0, 0],
            },
            columnStyles: {
              0: { cellWidth: 30 },
              1: { cellWidth: 40 },
              2: { cellWidth: 25, halign: "center" },
              3: { cellWidth: 40, halign: "right" },
              4: { cellWidth: 45, halign: "right" },
            },
            margin: { left: margin, right: margin },
          });

          yPos = pdf.lastAutoTable.finalY + 10;
        }
      }

      // Check if we need a new page
      if (yPos > 250) {
        pdf.addPage();
        yPos = 20;
      }

      // Line separator
      pdf.setLineWidth(0.5);
      pdf.line(margin, yPos, pageWidth - margin, yPos);
      yPos += 8;

      // Bill Summary
      pdf.setFontSize(10);
      pdf.setFont("helvetica", "normal");

      pdf.text("Subtotal (Without Tax):", margin, yPos);
      pdf.text(
        `${Store.CurrencySymbol}${subtotalWithoutTax.toFixed(2)}`,
        pageWidth - margin,
        yPos,
        { align: "right" }
      );
      yPos += 6;

      pdf.text("Total Tax:", margin, yPos);
      pdf.text(
        `${Store.CurrencySymbol}${(salesData.BillTaxAmount || 0).toFixed(2)}`,
        pageWidth - margin,
        yPos,
        { align: "right" }
      );
      yPos += 6;

      if (salesData.BillDiscount > 0) {
        pdf.text("Discount:", margin, yPos);
        pdf.text(
          `-${Store.CurrencySymbol}${(salesData.BillDiscount || 0).toFixed(2)}`,
          pageWidth - margin,
          yPos,
          { align: "right" }
        );
        yPos += 6;
      }

      // Line separator
      pdf.setLineWidth(0.3);
      pdf.line(margin, yPos, pageWidth - margin, yPos);
      yPos += 6;

      // Total Amount Paid
      pdf.setFontSize(12);
      pdf.setFont("helvetica", "bold");
      pdf.text("Amount Paid:", margin, yPos);
      pdf.text(
        `${Store.CurrencySymbol}${(salesData.BillAmount || 0).toFixed(2)}`,
        pageWidth - margin,
        yPos,
        { align: "right" }
      );
      yPos += 10;

      // Payment Details
      if (salesData.Payments && salesData.Payments.length > 0) {
        pdf.setFontSize(10);
        pdf.setFont("helvetica", "bold");
        pdf.text("Payment Method:", margin, yPos);
        yPos += 5;

        pdf.setFont("helvetica", "normal");
        salesData.Payments.forEach((payment) => {
          pdf.text(
            `${payment.PaymentMode}: ${Store.CurrencySymbol}${(
              payment.Amount || 0
            ).toFixed(2)}`,
            margin + 5,
            yPos
          );
          yPos += 5;
        });
        yPos += 5;
      }

      // Footer
      yPos += 10;
      pdf.setFontSize(10);
      pdf.setFont("helvetica", "bold");
      pdf.text("Thank you for shopping with us!", pageWidth / 2, yPos, {
        align: "center",
      });
      yPos += 5;
      pdf.setFont("helvetica", "normal");
      pdf.setFontSize(9);
      pdf.text("Please visit again. Have a great day!", pageWidth / 2, yPos, {
        align: "center",
      });

      // Save PDF
      pdf.save(`${Store.StoreName}-Receipt-${salesData.TransactionNo}.pdf`);
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("Failed to generate PDF. Please try again.");
    } finally {
      setIsDownloading(false);
    }
  };

  if (loading)
    return (
      <div className="flex justify-center items-center min-h-screen">
        <p className="text-lg">Loading...</p>
      </div>
    );
  if (!salesData)
    return (
      <div className="flex justify-center items-center min-h-screen">
        <p className="text-lg">No data found.</p>
      </div>
    );

  const {
    Customer,
    LineItems,
    Payments,
    TransactionNo,
    TransactionDate,
    BillAmount,
    BillDiscount,
    BillTaxAmount,
    TaxSummaryList,
  } = salesData;

  const subtotalWithoutTax = LineItems.reduce(
    (sum, item) => sum + item.SubTotalWithDiscount,
    0
  );

  return (
    <div className="min-h-screen bg-white px-4">
      {/* Invoice Container */}
      <div
        id="invoice-container"
        className="max-w-lg pb-2 mx-auto p-4 grid gap-4 bg-[#F1F6FA]"
      >
        {/* Header */}
        <div className="text-center pt-5 flex flex-col items-center px-4">
          <h1 className="text-xl font-bold text-gray-800 ">
            {Store?.StoreName} - E-RECEIPT
          </h1>

          {Store?.Logo && (
            <div className="w-16 h-16 rounded-full overflow-hidden flex items-center justify-center mr-4">
              {Store?.Logo?.ImagePath ? (
                <img
                  src={Store.Logo.ImagePath}
                  alt={Store?.Name || "User"}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-gray-200 flex items-center justify-center text-2xl font-extrabold text-gray-700">
                  {Store?.StoreName ? Store.StoreName[0].toUpperCase() : "U"}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Store Details Card */}
        <div className="">
          <div className="bg-white border p-3 border-gray-300 rounded-lg shadow-sm">
            <div className="px-4 pt-3">
              <a
                href={`https://${Store?.StoreName?.toLowerCase().replace(
                  /\s+/g,
                  ""
                )}.co.in`}
                className="text-sm text-blue-600 hover:underline block text-center"
              >
                https://{Store?.StoreName.toLowerCase().replace(/\s+/g, "")}
                .co.in
              </a>
            </div>
            <div className="px-4 py-3 text-center">
              <p className="text-sm font-semibold text-gray-800 mb-1">
                <span className="font-normal">Store Name:</span>{" "}
                {Store?.StoreName}
              </p>
            </div>
            <div className="px-4 py-3 bg-gray-200 text-center rounded-2xl">
              <p className="text-sm text-gray-700">
                {Store?.Address1} {Store?.Address2}, {Store?.City} {Store?.State}{" "}
                {Store?.PostalCode}
              </p>
            </div>
          </div>
        </div>

        {/* Customer Message */}
        <div className="bg-white border border-gray-300 shadow-xs rounded-t-2xl p-4">
          <h3 className="text-md font-bold text-gray-800 mb-2">
            Dear {Customer?.FirstName} {Customer?.LastName}
          </h3>
          <p className="text-sm text-gray-700 mb-3 leading-relaxed italic">
            Thank you for shopping with {Store?.StoreName}. Details of your
            transactions are mentioned below.
          </p>
          <p className="text-sm text-gray-600">{TransactionNo}</p>
        </div>

        <div className="bg-white">
          {/* Amount and Quantity and download */}
          <div className="bg-[#F8F1F1] p-4">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm text-gray-600 mb-1">Amount Paid</p>
                <p className="text-md font-bold text-gray-900">
                  {Store?.CurrencySymbol}
                  {BillAmount.toFixed(2)}
                </p>
              </div>

              <div className="text-right">
                <p className="text-sm text-gray-600 mb-1">Quantity</p>
                <p className="text-md font-bold text-gray-900">
                  {LineItems.reduce((sum, item) => sum + item.Qty, 0)}
                </p>
              </div>

              {/* Download Button - Fixed Position */}
              <div className="">
                <button
                  onClick={handleDownloadPDF}
                  disabled={isDownloading}
                  className="w-full sm:w-auto flex cursor-pointer items-center justify-center gap-2 border border-[#fabebe] hover:bg-[#f9b9b9] text-gray-700 font-semibold py-1.5 px-3 rounded-lg transition-all duration-200 disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {isDownloading ? (
                    <>
                      <svg
                        className="animate-spin h-7 w-7 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      {/* <span>Generating PDF...</span> */}
                    </>
                  ) : (
                    <>
                      <svg
                        className="w-7 h-7"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                      {/* <span>Download</span> */}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Order Date and Invoice */}
          <div className="p-2">
            <div className="bg-[#F1F6FA] rounded-t-md flex justify-between py-5 px-4">
              <div className="flex flex-col items-start">
                <span className="text-sm text-gray-700">
                  Order Date & Order Time
                </span>
                <span className="text-sm font-semibold text-gray-900">
                  {formatFirestoreTimestamp(salesData?.TransactionDate)}
                </span>
              </div>
              <div className="flex flex-col items-start">
                <span className="text-sm text-gray-700">GST Invoice No</span>
                <span className="text-sm font-semibold text-gray-900">
                  {TransactionNo}
                </span>
              </div>
            </div>
          </div>

          {/* Line Items */}
          <div className="grid bg-white rounded-md p-2 mt-1">
            {LineItems.map((item, idx) => {
              const taxAmount =
                item.TaxComponentAmount?.reduce(
                  (sum, t) => sum + t.TaxAmount,
                  0
                ) || 0;
              const isExpanded = expandedItems[idx];

              return (
                <div
                  key={idx}
                  className="bg-white py-2 pt-3 overflow-hidden border-t border-dashed border-gray-300"
                >
                  {/* Product Header */}
                  <div className="flex justify-between items-start px-4 bg-white">
                    <div className="flex-1">
                      <h4 className="text-sm font-bold text-gray-900 mb-1 flex items-center">
                        {item.Product?.Name}
                      </h4>
                      <p className="text-sm text-gray-500">
                        {item.Product?.SKU || "Not Avl"}
                      </p>
                    </div>
                    <div className="text-right ml-4">
                      <p className="text-base font-bold text-gray-900">
                        {Store?.CurrencySymbol}
                        {item.Amount.toFixed(2)}
                      </p>
                    </div>
                  </div>

                  {/* More Details Button */}
                  <div className="px-4 py-2 bg-white">
                    <button
                      onClick={() => toggleItemDetails(idx)}
                      className="text-sm text-blue-600 hover:underline flex items-center transition-all"
                    >
                      <span
                        className={`mr-1 transform transition-transform ${
                          isExpanded ? "rotate-180" : ""
                        }`}
                      >
                        ▼
                      </span>{" "}
                      {isExpanded ? "Hide Details" : "More Details"}
                    </button>
                  </div>

                  {/* Price Breakdown - Collapsible */}
                  {isExpanded && (
                    <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">
                          Base Price (Qty: {item.Qty})
                        </span>
                        <span className="text-gray-900 font-medium">
                          {Store?.CurrencySymbol}
                          {item.SubTotal.toFixed(2)}
                        </span>
                      </div>
                      {item.DiscountAmount > 0 && (
                        <div className="flex justify-between text-green-600">
                          <span>Discount ({item.DiscountPer}%)</span>
                          <span>
                            -{Store?.CurrencySymbol}
                            {item.DiscountAmount.toFixed(2)}
                          </span>
                        </div>
                      )}
                      {item.ChargeTaxOnProduct && taxAmount > 0 && (
                        <>
                          <div className="flex justify-between">
                            <span className="text-gray-600">
                              Price after discount
                            </span>
                            <span className="text-gray-900 font-medium">
                              {Store?.CurrencySymbol}
                              {item.SubTotalWithDiscount.toFixed(2)}
                            </span>
                          </div>
                          {item.TaxComponentAmount?.map((tax, i) => (
                            <div key={i} className="flex justify-between">
                              <span className="text-gray-600">
                                {tax.TaxName} ({tax.TaxPercentage}%)
                              </span>
                              <span className="text-gray-900 font-medium">
                                {Store?.CurrencySymbol}
                                {tax.TaxAmount.toFixed(2)}
                              </span>
                            </div>
                          ))}
                        </>
                      )}
                      <div className="flex justify-between pt-2 border-t border-gray-300 font-semibold">
                        <span className="text-gray-800">Item Total</span>
                        <span className="text-gray-900">
                          {Store?.CurrencySymbol}
                          {item.Amount.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="rounded-t-5xl overflow-hidden bg-[#F8F8FA] p-2">
            {/* Tax Information - Collapsible */}
            {TaxSummaryList && TaxSummaryList.length > 0 && (
              <div className="px-4 pt-1">
                <button
                  onClick={toggleTaxInfo}
                  className="text-sm text-blue-600 hover:underline mb-3 flex items-center transition-all"
                >
                  <span
                    className={`mr-1 transform transition-transform ${
                      expandedTaxInfo ? "rotate-180" : ""
                    }`}
                  >
                    ▼
                  </span>{" "}
                  {expandedTaxInfo ? "Hide Tax Information" : "Tax Information"}
                </button>
                {expandedTaxInfo && (
                  <div className="space-y-2 text-sm bg-white p-1 rounded-md mb-3">
                    {TaxSummaryList.map((tax, idx) => (
                      <div key={idx}>
                        <div className="flex justify-between font-semibold text-gray-800 mb-1">
                          <span>{tax.TaxGroup}</span>
                          <span>
                            {Store?.CurrencySymbol}
                            {tax.TotalTax.toFixed(2)}
                          </span>
                        </div>
                        {tax.TaxComponentAmount?.map((comp, i) => (
                          <div
                            key={i}
                            className="flex justify-between text-gray-600 pl-4"
                          >
                            <span>
                              {comp.TaxName} ({comp.TaxPercentage}%)
                            </span>
                            <span>
                              {Store?.CurrencySymbol}
                              {comp.TaxAmount.toFixed(2)}
                            </span>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Bill Summary */}
            <div className="rounded-lg overflow-hidden">
              <div className="px-4 pb-4 space-y-2 text-sm border-b border-gray-300">
                <div className="flex justify-between">
                  <span className="text-gray-700">Subtotal (Without Tax)</span>
                  <span className="font-semibold text-gray-900">
                    {Store?.CurrencySymbol}
                    {subtotalWithoutTax.toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-700">Total Tax</span>
                  <span className="font-semibold text-gray-900">
                    {Store?.CurrencySymbol}
                    {BillTaxAmount.toFixed(2)}
                  </span>
                </div>
              </div>
              <div className="bg-blue-600 text-white px-4 py-4 mt-3">
                <div className="flex justify-between items-center">
                  <span className="text-base font-bold">Amount Paid</span>
                  <span className="text-xl font-bold">
                    {Store?.CurrencySymbol}
                    {BillAmount.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mx-4 my-1 text-center">
          <p className="text-sm font-semibold text-gray-800">
            Thank you for shopping with us! 🙏
          </p>
          <p className="text-sm text-gray-600">
            Please visit again. Have a great day!
          </p>
        </div>
      </div>
    </div>
  );
};

export default PublicInvoicePage;
